import React from 'react'
import { Box, Stack, Typography, alpha } from '@mui/material'
import MailOutlineRoundedIcon from '@mui/icons-material/MailOutlineRounded'

import { HOLIDAYS_RADIUS } from '@/src/utils/holidays'

import PoweredByWrapper from '../poweredBy'

interface ContactStripProps {
    label: string
    phoneLabel: string
    emailAddress: string
    emailHref: string
    /**
     * `inline` puts the email at the end of the row and needs the full content width; `stacked`
     * drops it underneath, which is what the narrow desktop sidebar and a phone can carry.
     */
    layout: 'inline' | 'stacked'
    onContactClick: () => void
    isMobile: boolean
}

/**
 * The number, repeated where a reader is most likely to want it: before they start reading and
 * again once they have finished. Buried at the foot of the form it was reachable only by the
 * people who had already decided to fill the form in.
 */
const ContactStrip: React.FC<ContactStripProps> = ({
    label,
    phoneLabel,
    emailAddress,
    emailHref,
    layout,
    onContactClick,
    isMobile
}) => {
    const isInline = layout === 'inline'

    return (
        <Stack
            direction={isInline ? 'row' : 'column'}
            spacing={isInline ? 2 : 1.25}
            sx={theme => ({
                alignItems: isInline ? 'center' : 'flex-start',
                p: isMobile ? 1.5 : 2,
                borderRadius: HOLIDAYS_RADIUS.SURFACE,
                border: '1px solid',
                borderColor: 'borders.default',
                // The same wash as the "We're here to help" card: a near-white surface tinted
                // from brand blue into the secondary warm, rather than a flat blue panel.
                bgcolor: 'background.paper',
                backgroundImage: `linear-gradient(110deg, ${alpha(
                    theme.palette.primary.main,
                    0.07
                )} 0%, ${alpha(theme.palette.background.paper, 0)} 45%, ${alpha(
                    theme.palette.secondary.main,
                    0.07
                )} 100%)`
            })}
        >
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'textColors.subheading' }}>
                    {label}
                </Typography>
                <Typography
                    dir="ltr"
                    sx={{
                        fontSize: isMobile ? '1.0625rem' : '1.125rem',
                        fontWeight: 700,
                        // The number is one token; wrapping it across lines makes it unreadable.
                        whiteSpace: 'nowrap',
                        color: 'primary.main'
                    }}
                >
                    {phoneLabel}
                </Typography>
            </Box>

            <Stack
                direction="row"
                spacing={0.75}
                component="a"
                href={emailHref}
                onClick={onContactClick}
                sx={{
                    alignItems: 'center',
                    flexShrink: 0,
                    textDecoration: 'none',
                    color: 'primary.main',
                    fontSize: '0.875rem',
                    fontWeight: 600
                }}
            >
                <MailOutlineRoundedIcon sx={{ fontSize: 16 }} />
                <Box component="span" dir="ltr">
                    {emailAddress}
                </Box>
            </Stack>
            <PoweredByWrapper height={16} />
        </Stack>
    )
}

export { ContactStrip }
export default ContactStrip
